import { ValidatorFn, Validators } from '@angular/forms';
import { AvailabilityColumns, Column } from '@miq/catalog';
import { AvailabilitySpaceClass } from '../../../../../../models/space/availability-space.class';
import { formatDate, generateLastUpdatedByMessage } from '../../../../../../shared/utils/functions';
import { DetailsOverlayValidators } from '../detail-overlay.validators';
import { AreaUrl } from '../details-overlay.constants';
import { DetailsOverlayData, OverlayField } from '../details-overlay.model';

export const AVAILABILITY_CONFIG: DetailsOverlayData<AvailabilitySpaceClass> = {
  id: space => space.id.toString(),
  title: space => `${space.suiteNumber}`,
  validators: [
    {
      groupName: 'formGroupSpace',
      validator: [DetailsOverlayValidators.notEmptySpaceUOM, DetailsOverlayValidators.spaceSizeLessThanFloorSize],
    },
  ],
  subsets: [
    {
      id: AvailabilityColumns.FloorSuite_FloorNumber,
      dataField: 'floorNumber',
      inputType: 'textbox',
      displayFn: space => space.getFloorNumber(),
      validationFn: space => space.areFloorAndSuitRequiredValidation(),
      labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.FloorSuite_FloorNumber)?.header,
    },
    {
      id: AvailabilityColumns.FloorSuite_Suite,
      dataField: 'suiteNumber',
      inputType: 'textbox',
      displayFn: space => space.suiteNumber,
      validationFn: space => space.areFloorAndSuitRequiredValidation(),
      class: 'suite',
      labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.FloorSuite_Suite)?.header,
    },
    {
      id: AvailabilityColumns.SpaceDetails_AvailableSpace,
      dataField: 'spaceSize',
      formGroup: 'formGroupSpace',
      inputType: 'numeric',
      labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.SpaceDetails_AvailableSpace)?.header,
      unit: {
        dataField: 'spaceSizeUOM',
        formGroup: 'formGroupSpace',
        inputType: 'dropdown',
        lookupCategory: AreaUrl,
        displayFn: space => space.getAreaSpaceUOM(),
      },
      displayFn: space => space.getAreaSpace(),
    },
    {
      id: AvailabilityColumns.SpaceDetails_DateListed,
      dataField: 'onMarketDate',
      inputType: 'date',
      displayFn: space => formatDate(space.dateOnMarket),
      labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.SpaceDetails_DateListed)?.header,
    },
    {
      id: AvailabilityColumns.SpaceDetails_AvailabilityType,
      lookupCategory: '/lookup/ref?query=availability',
      dataField: 'availabilityType',
      inputType: 'dropdown',
      displayFn: space => space.availabilityType,
      labelFn: columns =>
        columns.find(col => col.columnId === AvailabilityColumns.SpaceDetails_AvailabilityType)?.header,
    },
    {
      id: AvailabilityColumns.SpaceDetails_DateAvailable,
      dataField: 'dateAvailable',
      inputType: 'date',
      displayFn: space => formatDate(space.dateAvailable),
      labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.SpaceDetails_DateAvailable)?.header,
    },
    /** TODO (Aurelio): Lease Type needs to be added to the EDP's Availability Info contract before implementing this.
     * {
     *   id: AvailabilityColumns.SpaceDetails_LeaseType,
     *   lookupCategory: '/lookup/ref/country?lookupcategory=MIQ_LEASE_TYPE',
     *   dataField: 'leaseType',
     *   displayFn: () => 'leaseType',
     *   inputType: 'dropdown',
     *   labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.SpaceDetails_LeaseType)?.header,
     * },
     */

    {
      id: 379,
      displayFn: space => space.generateListingRepAgency(),
      labelFn: columns =>
        columns.find(col => col.columnId === AvailabilityColumns.Companies_ListingAgency)?.header || 'Listing Agency',
    },
    {
      id: AvailabilityColumns.SpaceDetails_AvailabilityStatus,
      dataField: 'availabilityStatus',
      displayFn: space => space.getSpaceAvailabilityStatus(),
      inputType: 'dropdown',
      lookupCategory: '/lookup/availabilitystatus',
      labelFn: columns =>
        columns.find(col => col.columnId === AvailabilityColumns.SpaceDetails_AvailabilityStatus)?.header,
    },
    {
      id: AvailabilityColumns.SpaceDetails_Vacant,
      dataField: 'vacant',
      inputType: 'dropdown',
      lookupCategory: '/lookup/yn',
      displayFn: space => space.vacantF,
      validators: [DetailsOverlayValidators.isAvailabilityVacant],
      labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.SpaceDetails_Vacant)?.header,
    },
    /**
     * TODO (Aurelio): Possession needs to be added to the EDP's Availability Info contract before this is implemented.
     * {
     *   id: AvailabilityColumns.Terms_Possession,
     *   dataField: 'possession',
     *   inputType: 'dropdown',
     *   lookupCategory: '/lookup/ref?query=possession',
     *   displayFn: () => 'possession',
     *   labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.Terms_Possession)?.header,
     * },
     */
    {
      id: AvailabilityColumns.Asking_Rental_RateYearly,
      dataField: 'rentRate',
      inputType: 'currency',
      formGroup: 'formGroupRentRate',
      unit: {
        dataField: 'rentRateUOM',
        inputType: 'dropdown',
        formGroup: 'formGroupRentRate',
        lookupCategory: '/lookup/ref/country?lookupcategory=currency',
        displayFn: space => space.generateRentRateUOM(),
      },
      displayFn: space => space.generateRentRateValue(),
      labelFn: columns => columns.find(col => col.columnId === AvailabilityColumns.Terms_AskingRateYearly)?.header,
    },
  ],
  notesUpdate: [
    {
      labelFn: columns => {
        return columns.find(col => col.columnId === AvailabilityColumns.SpaceDetails_ListingNotes)?.header;
      },
      dataField: 'notes',
      inputType: 'textarea',
      displayFn: space => space.listingNotes,
      class: 'notes',
    },
  ],
  footer: {
    label: 'overlayDetails.lastUpdate',
    class: 'last-update',
    displayFn: space => generateLastUpdatedByMessage(space.edpLastUpdateDate, space.edpLastUpdateBy),
  },
};

export function filterAndSetAvailability(
  space: AvailabilitySpaceClass,
  catalogSections: Column[],
  subsetsConfig: OverlayField<AvailabilitySpaceClass>[]
): OverlayField<AvailabilitySpaceClass>[] {
  const catalogIds = catalogSections.reduce((acc, { columnId, required }) => {
    return [...acc, { columnId, required }];
  }, []);
  return subsetsConfig
    .filter(set => catalogIds.map(catalog => catalog.columnId).includes(set.id))
    .reduce((acc, filteredSubset) => {
      const isCatalogRequired = catalogIds.find(field => field.columnId === filteredSubset.id);
      const subsetContainsValidationFn = typeof filteredSubset.validationFn === 'function';
      const isRequiredByFn = subsetContainsValidationFn ? filteredSubset.validationFn(space) : false;
      const subsetContainsValidators = filteredSubset.validators?.length > 0;
      let validators = [];
      if (subsetContainsValidators) {
        filteredSubset.validators.forEach(validator => {
          validators = pushValidations(filteredSubset, validator);
        });
      }
      if (isRequiredByFn || (isCatalogRequired.required && !subsetContainsValidationFn)) {
        validators = pushValidations(filteredSubset, Validators.required);
      }
      return [...acc, { ...filteredSubset, validators }];
    }, []);
}

function pushValidations(subsetRef: OverlayField<any>, validator: ValidatorFn): Array<Function> {
  const validatorsArr = subsetRef.validators ?? [];
  validatorsArr.push(validator);
  return Array.from(new Set(validatorsArr));
}
